import { useEffect, useState, type FormEventHandler as SubmitEventHandler } from "react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { AuthPageLayout } from "../components/layout/PageLayouts";
import { useAuth } from "../lib/auth-context";

export default function LoginPage() {
	const { login } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const API_URL = (import.meta.env.VITE_API_URL || "http://localhost:3001").replace(/\/+$/, "");
	const routeState = location.state as { prefillEmail?: string } | null;
	const initialEmail = routeState?.prefillEmail?.trim() || "";
	const searchParams = new URLSearchParams(location.search);
	const oauthError = searchParams.get("error");
	const [email, setEmail] = useState(initialEmail);
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!oauthError || typeof window === "undefined") return;
		window.sessionStorage.removeItem("oauth:google:pending");
	}, [oauthError]);

	const onSubmit: SubmitEventHandler<HTMLFormElement> = async (e) => {
		e.preventDefault();
		setError("");
		setLoading(true);
		try {
			await login(email, password);
			navigate("/dashboard");
		} catch (err) {
			setError(err instanceof Error ? err.message : "Login failed");
		} finally {
			setLoading(false);
		}
	};

	return (
		<AuthPageLayout>
			<Card>
				<CardHeader>
					<CardTitle>Login</CardTitle>
				</CardHeader>
				<CardContent>
					{oauthError ? (
						<p className="mb-4 rounded-md bg-red-50 p-2 text-sm text-red-600">
							Google sign-in failed. Please try again.
						</p>
					) : null}
					<Button asChild variant="outline" className="mb-4 w-full">
						<a
							href={`${API_URL}/auth/google/start?returnTo=${encodeURIComponent("/dashboard")}`}
							onClick={() => {
								if (typeof window === "undefined") return;
								window.sessionStorage.setItem("oauth:google:pending", "1");
							}}
						>
							<svg aria-hidden="true" viewBox="0 0 24 24" className="mr-2 h-4 w-4">
								<path
									fill="#4285F4"
									d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.3h6.45a5.52 5.52 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.56-5.16 3.56-8.66z"
								/>
								<path
									fill="#34A853"
									d="M12 24c3.24 0 5.96-1.07 7.95-2.9l-3.88-3A7.2 7.2 0 0 1 12 19.4a7.2 7.2 0 0 1-6.77-4.98H1.22v3.12A12 12 0 0 0 12 24z"
								/>
								<path
									fill="#FBBC05"
									d="M5.23 14.42A7.2 7.2 0 0 1 4.83 12c0-.84.14-1.66.4-2.42V6.46H1.22A12 12 0 0 0 0 12c0 1.94.46 3.78 1.22 5.54l4-3.12z"
								/>
								<path
									fill="#EA4335"
									d="M12 4.6c1.76 0 3.34.6 4.58 1.78l3.43-3.43C17.95 1.07 15.24 0 12 0A12 12 0 0 0 1.22 6.46l4.01 3.12A7.2 7.2 0 0 1 12 4.6z"
								/>
							</svg>
							Continue with Google
						</a>
					</Button>
					<form className="space-y-4" onSubmit={onSubmit}>
						<label className="block text-sm font-semibold text-slate-700">
							Email
							<input
								type="email"
								className="mt-2 w-full rounded-md border border-slate-200 p-2"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								autoComplete="email"
								required
							/>
						</label>
						<label className="block text-sm font-semibold text-slate-700">
							Password
							<input
								type="password"
								className="mt-2 w-full rounded-md border border-slate-200 p-2"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								autoComplete="current-password"
								required
								minLength={8}
							/>
						</label>
						{error && <p className="rounded-md bg-red-50 p-2 text-sm text-red-600">{error}</p>}
						<Button type="submit" disabled={loading} className="w-full">
							{loading ? "Signing in..." : "Sign in"}
						</Button>
					</form>
					<div className="mt-4 flex items-center justify-between text-sm text-slate-600">
						<p>
							No account?{" "}
							<NavLink
								to="/register"
								state={email.trim() ? { prefillEmail: email.trim() } : undefined}
								className="font-semibold text-ink"
							>
								Register
							</NavLink>
						</p>
						<NavLink to="/forgot-password" className="font-semibold text-ink">
							Forgot password?
						</NavLink>
					</div>
				</CardContent>
			</Card>
		</AuthPageLayout>
	);
}
